import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Camera, Check, ChevronLeft, ChevronRight, Clock, Pencil, SlidersHorizontal, Sparkles, Users, Zap } from 'lucide-react';
import { RulesEditor, StaplesEditor } from '../components/Editors';
import { IngredientPicker } from '../components/IngredientPicker';
import { ChipSet, DislikesEditor, GoalsPicker, HouseholdRow, PriorityPicker, Question, TimeControl } from '../components/Prefs';
import { BasicsNudge } from '../components/BasicsNudge';
import { Macros, MealCard, MealRow } from '../components/MealCards';
import { RecipeImage } from '../components/RecipeImage';
import { Notice, Sheet, Steps, Switch } from '../components/ui';
import { ruleLabel, type DietRuleId } from '../domain/diet';
import { rule } from '../domain/knowledge';
import { showcaseRecipe } from '../domain/likes';
import { buildSummary, pickFirstMeal, type SummaryStep } from '../domain/onboarding';
import { FOOD_LIKES, FRUSTRATIONS, TIME_OPTIONS, type FoodLike, type TimeChoice } from '../domain/preferences';
import { recommend } from '../domain/recommend';
import { DEFAULT_STAPLES } from '../domain/staples';
import { type IngredientId, labelName } from '../domain/taxonomy';
import { track } from '../lib/analytics';
import { profileSchema, type Profile } from '../state/schema';
import { useStore } from '../state/store';
import { AmariLogo, AmariMark } from '../components/Logo';

/** Not onboarded yet. Marks onboarding done quietly: the first visit is about food, not forms. */
function useStart() {
  const navigate = useNavigate();
  const complete = useStore((s) => s.completeOnboarding);
  const consent = useStore((s) => s.consent.analytics);
  return (to: string, path: string, patch: Partial<Profile> = {}) => {
    track('onboarding_started', { path });
    complete(patch, consent);
    session.set(FIRST_RUN_KEY, '1');
    session.del(FIRST_MEAL_KEY);
    navigate(to);
  };
}

/** After the splash: two ways in, asked by Amari. In a hurry goes straight to food; setup asks five short questions. */
export function Welcome() {
  const navigate = useNavigate();
  const name = useStore((s) => s.profile.name);
  return (
    <main className="screen min-h-[100dvh] flex flex-col fade-in">
      <div className="flex-1 flex flex-col justify-center max-w-[520px] mx-auto w-full">
        <AmariLogo size={44} className="mb-8" />
        <h1 className="text-[40px] leading-[46px] font-bold tracking-[-0.03em] mb-4">Hungry now?</h1>
        <div className="flex items-start gap-3 mb-8">
          <AmariMark size={40} />
          <p className="flex-1 rounded-[20px] rounded-tl-[6px] bg-fill px-4 py-3 text-body-lg">
            {name ? `Welcome back, ${name}. ` : 'Hi, I’m Amari. '}Show me what you have and I&rsquo;ll show you every meal you can make. How do you want to start?
          </p>
        </div>
        <div className="grid gap-3">
          <StartChoice primary icon={<Zap size={22} />} title="I’m in a hurry" onClick={() => navigate('/setup/hurry')}
            detail="Tell me what you can’t eat, snap your fridge, see what you can make. About a minute." />
          <StartChoice icon={<SlidersHorizontal size={22} />} title="Set up my preferences first" onClick={() => navigate('/setup/1')}
            detail="Five quick questions so every pick fits your tastes. About two minutes." />
        </div>
      </div>
      <p className="text-footnote text-center text-fg-muted max-w-[520px] mx-auto w-full pt-6" style={{ paddingBottom: 'calc(var(--safe-bottom) + 12px)' }}>Free. Photos are never stored.</p>
    </main>
  );
}

function StartChoice({ icon, title, detail, primary = false, onClick }: { icon: React.ReactNode; title: string; detail: string; primary?: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex items-center gap-4 w-full text-left rounded-[20px] p-4 min-h-[88px] active:scale-[0.98] transition-transform ${primary ? 'bg-primary text-primary-on' : 'bg-fill text-fg'}`}>
      <span className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${primary ? 'bg-white/20' : 'bg-surface text-primary'}`} aria-hidden>{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-headline">{title}</span>
        <span className={`block text-subhead mt-0.5 ${primary ? 'text-primary-on/85' : 'text-fg-muted'}`}>{detail}</span>
      </span>
      <ChevronRight size={20} className={primary ? 'text-primary-on/85' : 'text-fg-muted'} aria-hidden />
    </button>
  );
}

/** In a hurry, step one: the no-gos, for health. Hard rules, so they're asked before any food is shown. */
function Hurry() {
  const navigate = useNavigate();
  const start = useStart();
  const [rules, setRules] = useState<DietRuleId[]>([]);
  const [other, setOther] = useState('');
  const none = !rules.length && !other.trim();
  const go = () => {
    track('onboarding_step', { step: 'hurry_rules', rules: rules.length });
    start('/scan', 'hurry', { rules, otherAllergies: other.trim() });
  };
  return (
    <main className="min-h-[100dvh] flex flex-col">
      <div className="screen flex-1 max-w-[560px] mx-auto w-full fade-in">
        <div className="flex items-center gap-2 -ml-2 mb-4">
          <button className="icon-btn text-primary" onClick={() => navigate('/welcome')} aria-label="Back"><ChevronLeft size={28} aria-hidden /></button>
        </div>
        <h1 className="text-title-1 mb-2">First, any no-gos?</h1>
        <p className="text-body text-fg-muted mb-6">For your health. We&rsquo;ll never show a meal with these, checked against every ingredient. Then snap your fridge.</p>
        <RulesEditor rules={rules} other={other} onChange={(r, o) => { setRules(r); setOther(o); }} />
        <div className="mt-6"><Notice>Brands and kitchens vary. <strong>Always check labels</strong> if an allergy is serious.</Notice></div>
      </div>
      <div className="sticky-actions">
        <div className="max-w-[560px] mx-auto">
          <button className="btn btn-primary w-full" onClick={go}><Camera size={20} aria-hidden /> {none ? 'Nothing to avoid, snap my fridge' : 'Continue to snap my fridge'}</button>
        </div>
      </div>
    </main>
  );
}

type Draft = Profile & { kitchen: IngredientId[]; analytics: boolean };
const DRAFT_KEY = 'snapcook_onboarding_draft';
const FIRST_MEAL_KEY = 'snapcook_first_meal';
/** Set while a new person goes from the home screen to their first results. */
export const FIRST_RUN_KEY = 'snapcook_first_run';
const TOTAL = 5;
const STEP_OF: Record<SummaryStep, number> = { struggles: 1, food: 2, kitchen: 3, avoid: 4, priorities: 5 };
const STEP_NAMES = ['struggles', 'food', 'kitchen', 'avoid', 'priorities'] as const;

/** Everyday items offered as one-tap chips; anything else is a search away. */
const COMMON: IngredientId[] = ['eggs', 'chicken_breast', 'white_rice', 'pasta', 'potatoes', 'onion', 'garlic', 'tomatoes',
  'canned_tomatoes', 'ground_beef', 'black_beans', 'cheddar', 'milk', 'bell_pepper', 'spinach', 'carrots'];

const session = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k: string) => { try { sessionStorage.removeItem(k); } catch { /* ignore */ } },
};

function loadDraft(base: Profile, kitchen: IngredientId[]): Draft {
  try {
    const raw = JSON.parse(session.get(DRAFT_KEY) ?? 'null');
    if (raw) {
      const p = profileSchema.safeParse(raw);
      if (p.success) return { ...p.data, kitchen: Array.isArray(raw.kitchen) ? raw.kitchen : [], analytics: raw.analytics === true };
    }
  } catch { /* ignore */ }
  return { ...base, kitchen, analytics: false };
}

export function Setup() {
  const { step: stepParam = '1' } = useParams();
  if (stepParam === 'summary' || stepParam === 'ready') return <Summary />;
  if (stepParam === 'meal') return <Results />;
  if (stepParam === 'have') return <WhatYouHave />;
  if (stepParam === 'hurry') return <Hurry />;
  return <Questions step={Math.min(TOTAL, Math.max(1, Number(stepParam) || 1))} />;
}

/** "Type what I have": one-tap chips for everyday food, search for the rest, then meals. */
function WhatYouHave() {
  const navigate = useNavigate();
  const onboarded = useStore((s) => s.onboarded);
  const kitchen = useStore((s) => s.kitchen.items);
  const addKitchenItem = useStore((s) => s.addKitchenItem);
  const [picked, setPicked] = useState<IngredientId[]>(() => kitchen.map((k) => k.ingredientId));
  if (!onboarded) return <Navigate to="/welcome" replace />;
  const toggle = (id: IngredientId) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const go = () => {
    const have = new Set(kitchen.map((k) => k.ingredientId));
    for (const id of picked) if (!have.has(id)) addKitchenItem(id);
    track('onboarding_step', { step: 'typed_kitchen', items: picked.length });
    navigate('/setup/meal', { replace: true });
  };
  return (
    <main className="min-h-[100dvh] flex flex-col">
      <div className="screen flex-1 max-w-[560px] mx-auto w-full fade-in">
        <div className="flex items-center gap-2 -ml-2 mb-4">
          <button className="icon-btn text-primary" onClick={() => navigate(-1)} aria-label="Back"><ChevronLeft size={28} aria-hidden /></button>
        </div>
        <h1 className="text-title-1 mb-2">What have you got?</h1>
        <p className="text-body text-fg-muted mb-6">Tap a few things. Even three is enough.</p>
        <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Common foods">
          {[...COMMON, ...picked.filter((k) => !COMMON.includes(k))].map((id) => (
            <button key={id} type="button" className="chip" aria-pressed={picked.includes(id)} onClick={() => toggle(id)}>{labelName(id)}</button>
          ))}
        </div>
        <IngredientPicker label="Search for something else" placeholder="e.g. salmon, tofu, lentils" exclude={picked} onPick={(id) => setPicked((p) => [...p, id])} />
      </div>
      <div className="sticky-actions">
        <div className="max-w-[560px] mx-auto">
          <button className="btn btn-primary w-full" disabled={!picked.length} onClick={go}>
            {picked.length ? `Find meals with ${picked.length} ${picked.length === 1 ? 'item' : 'items'}` : 'Tap what you have'}
          </button>
        </div>
      </div>
    </main>
  );
}

function Questions({ step }: { step: number }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editing = params.get('edit') === '1';
  const profile = useStore((s) => s.profile);
  const kitchenItems = useStore((s) => s.kitchen.items);
  const complete = useStore((s) => s.completeOnboarding);
  const addKitchenItem = useStore((s) => s.addKitchenItem);
  const [d, setD] = useState<Draft>(() => loadDraft(profile, kitchenItems.map((k) => k.ingredientId)));
  const [showGoal, setShowGoal] = useState(d.goals.length > 0 || d.priorities.includes('fitness'));

  // Answers survive going back, reloads and the browser back button.
  useEffect(() => { session.set(DRAFT_KEY, JSON.stringify(d)); }, [d]);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const commit = () => {
    const { kitchen, analytics, ...patch } = d;
    complete(patch, analytics);
    for (const id of kitchen) addKitchenItem(id);
  };
  const advance = (skipped: boolean) => {
    track('onboarding_step', { step: STEP_NAMES[step - 1], skipped });
    if (editing) { commit(); navigate('/setup/summary', { replace: true }); return; }
    if (step < TOTAL) { navigate(`/setup/${step + 1}`); return; }
    commit();
    track('onboarding_done', { rules: d.rules.length, goals: d.goals.length, kitchen: d.kitchen.length, likes: d.likes.length });
    navigate('/setup/summary', { replace: true });
  };
  // Skipping a step clears it, so nothing is assumed from answers the user didn't give.
  const skip = () => {
    const cleared: Partial<Draft>[] = [
      { frustrations: [], maxMinutes: 120 }, { likes: [] }, { kitchen: [], staples: [] }, { rules: [], otherAllergies: '', dislikes: [] }, { priorities: [], goals: [] },
    ];
    set(cleared[step - 1]);
    setTimeout(() => advance(true));
  };
  const back = () => (editing ? navigate('/setup/summary') : step === 1 ? navigate('/welcome') : navigate(`/setup/${step - 1}`));
  const titles = ['What gets in the way?', 'What do you enjoy eating?', 'What’s in your kitchen?', 'Anything to avoid?', 'What matters most?'];

  return (
    <main className="min-h-[100dvh] flex flex-col">
      <div className="screen flex-1 max-w-[560px] mx-auto w-full">
        <div className="flex items-center gap-2 -ml-2 mb-4">
          <button className="icon-btn text-primary" onClick={back} aria-label="Back"><ChevronLeft size={28} aria-hidden /></button>
          <div className="flex-1"><Steps step={step} total={TOTAL} /></div>
        </div>
        <h1 className="text-title-1 mb-2">{titles[step - 1]}</h1>

        {step === 1 && (
          <>
            <p className="text-body text-fg-muted mb-7">Choose any that apply. We&rsquo;ll shape your picks around them.</p>
            <Question title="Your biggest cooking frustrations">
              <ChipSet value={d.frustrations} onChange={(frustrations) => set({ frustrations })} options={FRUSTRATIONS} />
            </Question>
            <Question title="How many minutes do you usually have to cook?"><TimeControl value={d.maxMinutes} onChange={(maxMinutes) => set({ maxMinutes })} /></Question>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-body text-fg-muted mb-6">Pick as many as you like.</p>
            <FoodTiles value={d.likes} onChange={(likes) => set({ likes })} />
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-body text-fg-muted mb-7">Add a few things you have now, or skip and add your kitchen later. We only use what you tell us.</p>
            <Question title="Tap what you have">
              <div className="flex flex-wrap gap-2 mb-3">
                {COMMON.map((id) => {
                  const on = d.kitchen.includes(id);
                  return <button key={id} type="button" className="chip" aria-pressed={on} onClick={() => set({ kitchen: on ? d.kitchen.filter((x) => x !== id) : [...d.kitchen, id] })}>{labelName(id)}</button>;
                })}
                {d.kitchen.filter((k) => !COMMON.includes(k)).map((k) => (
                  <button key={k} type="button" className="chip" aria-pressed onClick={() => set({ kitchen: d.kitchen.filter((x) => x !== k) })}>{labelName(k)}</button>
                ))}
              </div>
              <IngredientPicker label="Search for something else" placeholder="e.g. salmon, tofu, lentils" exclude={d.kitchen} onPick={(id) => set({ kitchen: [...d.kitchen, id] })} />
            </Question>
            <Question title="Cupboard basics you always keep" hint="Counted as owned for every recipe.">
              {d.staples.length === 0 && (
                <button type="button" className="btn btn-sm btn-tonal mb-3" onClick={() => set({ staples: DEFAULT_STAPLES })}>Select the usual basics</button>
              )}
              <StaplesEditor staples={d.staples} onChange={(staples) => set({ staples })} />
            </Question>
            <Question title="Cooking for"><HouseholdRow value={d.household} onChange={(household) => set({ household })} /></Question>
          </>
        )}

        {step === 4 && (
          <>
            <p className="text-body text-fg-muted mb-6">Allergies and diets are hard rules, checked against every ingredient in code. Dislikes just keep a food off your plate.</p>
            <RulesEditor rules={d.rules} other={d.otherAllergies} onChange={(rules, otherAllergies) => set({ rules, otherAllergies })} />
            <div className="mt-7">
              <Question title="Foods you’d rather not eat" hint="Not an allergy, just not for you.">
                <DislikesEditor value={d.dislikes} onChange={(dislikes) => set({ dislikes })} />
              </Question>
            </div>
            <Notice>Brands and kitchens vary. <strong>Always check labels</strong> if an allergy is serious.</Notice>
          </>
        )}

        {step === 5 && (
          <>
            <p className="text-body text-fg-muted mb-7">Pick up to two. We&rsquo;ll lean on these when two dinners are otherwise close.</p>
            <PriorityPicker value={d.priorities} onChange={(priorities) => { set({ priorities }); if (priorities.includes('fitness')) setShowGoal(true); }} />
            <div className="mt-7">
              {!showGoal ? (
                <button type="button" className="text-primary font-semibold min-h-[44px]" onClick={() => setShowGoal(true)}>Add a nutrition or fitness goal (optional)</button>
              ) : (
                <Question title="Nutrition or fitness goal" hint="Optional. Goals only change which dinners rank higher. We never set calorie targets or ask for your weight here.">
                  <GoalsPicker value={d.goals} onChange={(goals) => set({ goals })} />
                  {d.goals.includes('lose') && <div className="mt-4"><Notice tone="info">{rule('gradual_pace').userText} {rule('support_resources').userText}</Notice></div>}
                </Question>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 mt-6">
              <span><span className="block text-body">Share anonymous usage stats</span><span className="block text-subhead text-fg-muted">Which screens you use. Never photos, allergies, goals or weight.</span></span>
              <Switch label="Share anonymous usage stats" checked={d.analytics} onChange={(analytics) => set({ analytics })} />
            </div>
          </>
        )}
      </div>

      <div className="sticky-actions">
        <div className="max-w-[560px] mx-auto flex gap-2">
          {!editing && <button className="btn btn-secondary" onClick={skip}>Skip</button>}
          <button className="btn btn-primary flex-1" onClick={() => advance(false)}>
            {editing ? 'Save' : step === 4 && !d.rules.length && !d.otherAllergies && !d.dislikes.length ? 'Nothing to avoid' : step === TOTAL ? 'See my meals' : 'Continue'}
          </button>
        </div>
      </div>
    </main>
  );
}

/** Food choices as photo tiles, each showing a real recipe from the catalog. */
function FoodTiles({ value, onChange }: { value: FoodLike[]; onChange: (v: FoodLike[]) => void }) {
  const toggle = (id: FoodLike) => {
    if (id === 'unsure') return onChange(value.includes('unsure') ? [] : ['unsure']);
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value.filter((x) => x !== 'unsure'), id]);
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Foods you enjoy">
      {FOOD_LIKES.map((f) => {
        const on = value.includes(f.id);
        const photo = f.id === 'unsure' ? null : showcaseRecipe(f.id);
        return (
          <button key={f.id} type="button" aria-pressed={on} onClick={() => toggle(f.id)} aria-label={f.id === 'african_caribbean' ? `${f.label}, coming soon` : f.label}
            className={`relative text-left rounded-[16px] overflow-hidden transition-shadow ${on ? 'ring-[3px] ring-primary ring-offset-2 ring-offset-bg' : ''}`}>
            {photo ? <RecipeImage recipeId={photo} ratio="4/3" rounded="rounded-none" /> : (
              <span className="block aspect-[4/3] bg-fill" aria-hidden />
            )}
            <span className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/75 to-transparent text-white">
              <span className="block text-subhead font-semibold leading-tight">{f.label}</span>
              {f.id === 'african_caribbean' && <span className="block text-caption text-white/85">Coming soon</span>}
            </span>
            <span className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${on ? 'bg-primary text-primary-on' : 'bg-black/35 border-2 border-white/90'}`} aria-hidden>
              {on && <Check size={14} strokeWidth={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Summary() {
  const navigate = useNavigate();
  const onboarded = useStore((s) => s.onboarded);
  const profile = useStore((s) => s.profile);
  const kitchenCount = useStore((s) => s.kitchen.items.length);
  const lines = useMemo(() => buildSummary(profile, kitchenCount), [profile, kitchenCount]);
  useEffect(() => { if (onboarded) track('summary_viewed', { lines: lines.length }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!onboarded) return null;

  return (
    <main className="min-h-[100dvh] flex flex-col">
      <div className="screen flex-1 max-w-[560px] mx-auto w-full fade-in">
        <div className="w-12 h-12 rounded-full bg-primary text-primary-on flex items-center justify-center mb-5 mt-6" aria-hidden><Sparkles size={22} /></div>
        <h1 className="text-title-1 mb-2">Here&rsquo;s how we&rsquo;ll personalize your meals</h1>
        <p className="text-body text-fg-muted mb-7">From your answers. Tap any line to change it.</p>
        {lines.length ? (
          <ul className="grid gap-1 mb-8" aria-label="What we learned">
            {lines.map((l) => (
              <li key={l.text}>
                <Link to={`/setup/${STEP_OF[l.step]}?edit=1`} className="flex items-center gap-3 py-2.5 min-h-[48px] group">
                  <Check size={18} className="text-primary shrink-0" aria-hidden />
                  <span className="flex-1 text-body">{l.text}</span>
                  <Pencil size={15} className="text-fg-muted shrink-0" aria-label="Edit" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-body mb-8">Nothing yet beyond what you can&rsquo;t eat. We learn from what you pick and skip. Want sharper picks now? <Link to="/setup/1?edit=1" className="text-primary font-semibold">Answer a few questions</Link></p>
        )}
        <ul className="grid gap-2 text-subhead text-fg-muted mb-8">
          <li>Allergies and diets are always enforced. Everything else just ranks dinners.</li>
          <li>Change any of this later in Profile.</li>
        </ul>
      </div>
      <div className="sticky-actions">
        <div className="max-w-[560px] mx-auto grid gap-2">
          <button className="btn btn-primary" onClick={() => navigate('/setup/meal')}>Show my first meal</button>
        </div>
      </div>
    </main>
  );
}

/** One-tap safety filters on the results; the full list is a tap away. Hard rules, enforced in code. */
const QUICK_RULES: DietRuleId[] = ['allergy_peanut', 'allergy_tree_nut', 'allergy_milk', 'gluten_free', 'vegetarian', 'vegan', 'halal'];

/** Every meal type counts: "what can I make" means breakfasts, sides and snacks too, not only dinners. */
const ALL_MEALS = ['main', 'breakfast', 'side', 'snack'] as const;
/** One-ingredient-away meals shown before "Show all". */
const ONE_AWAY_PREVIEW = 6;

/**
 * The first results: the best dinner, then EVERY meal they can make now (all meal types, never a
 * shortlist), then those one ingredient away. Narrowed only by time and what they can't eat (hard
 * rules, enforced in code). Opening a recipe asks for a free account.
 */
function Results() {
  const navigate = useNavigate();
  const onboarded = useStore((s) => s.onboarded);
  const profile = useStore((s) => s.profile);
  const kitchen = useStore((s) => s.kitchen.items);
  const updateProfile = useStore((s) => s.updateProfile);
  const recs = useMemo(() => recommend({ profile, kitchen: kitchen.map((k) => k.ingredientId), useSoon: [], recentlyCooked: [], stock: kitchen, meals: [...ALL_MEALS] }), [profile, kitchen]);
  const [maxTime, setMaxTime] = useState<TimeChoice>(() => TIME_OPTIONS.find((t) => t.value === profile.maxMinutes)?.value ?? 120);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [allOneAway, setAllOneAway] = useState(false);
  const known = kitchen.length > 0;
  const fits = useMemo(() => recs.filter((r) => maxTime >= 120 || r.recipe.minutes <= maxTime), [recs, maxTime]);
  const pool = useMemo(() => {
    const all = fits.filter((r) => r.recipe.meal === 'main');
    const mains = all.length ? all : recs.filter((r) => r.recipe.meal === 'main');
    // Hungry now: makeable first (in the recommender's order), then fewest missing, then quickest.
    if (!known) return mains;
    // Something they can make always leads: a dinner if one is covered, else any meal that is.
    const readyMains = mains.filter((r) => r.makeable);
    const ready = readyMains.length ? readyMains : fits.filter((r) => r.makeable);
    const rest = mains.filter((r) => !r.makeable).sort((x, y) => x.missing.length - y.missing.length || x.recipe.minutes - y.recipe.minutes);
    return [...ready, ...rest];
  }, [fits, recs, known]);
  const first = useMemo(() => pickFirstMeal(pool), [pool]);
  const ready = useMemo(() => (known ? fits.filter((r) => r.makeable) : []), [fits, known]);
  const oneAway = useMemo(() => (known ? fits.filter((r) => !r.makeable && r.missing.length === 1) : []), [fits, known]);

  useEffect(() => {
    if (first) track('first_meal_shown', { recipe: first.rec.recipe.id, state: first.state, results: pool.length, ready: ready.length });
    session.del(DRAFT_KEY);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!onboarded) return <Navigate to="/welcome" replace />;

  const title = !known ? 'Dinners to start with' : ready.length ? `You can make ${ready.length} ${ready.length === 1 ? 'meal' : 'meals'} now` : 'Closest to what you have';
  const changeTime = (t: TimeChoice) => { setMaxTime(t); updateProfile({ maxMinutes: t }); };
  const toggleRule = (id: DietRuleId) => updateProfile({ rules: profile.rules.includes(id) ? profile.rules.filter((x) => x !== id) : [...profile.rules, id] });
  const open = (id: string) => {
    session.set('snapcook_first_opened', '1');
    track('first_meal_opened', { recipe: id });
    navigate(`/recipe/${id}`);
  };
  const opened = (id: string) => () => { session.set('snapcook_first_opened', '1'); track('first_meal_opened', { recipe: id }); };
  const extraRules = profile.rules.filter((r) => !QUICK_RULES.includes(r));
  const restReady = ready.filter((r) => r.recipe.id !== first?.rec.recipe.id);

  return (
    <main className="min-h-[100dvh] flex flex-col">
      <div className="screen flex-1 max-w-[640px] mx-auto w-full fade-in">
        <h1 className="text-title-1 mb-4 mt-2">{title}</h1>

        {/* One scrolling row each, so the food stays above the fold. */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 mb-2" role="group" aria-label="How long have you got?">
          {TIME_OPTIONS.map((t) => (
            <button key={t.value} type="button" className="chip shrink-0" aria-pressed={maxTime === t.value} onClick={() => changeTime(t.value)}>
              {t.value === 120 ? 'Any time' : `Under ${t.value} min`}
            </button>
          ))}
        </div>
        <div className="mb-5" role="group" aria-label="Avoiding anything?">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-5 px-5">
            <span className="text-subhead text-fg-muted shrink-0">Avoid:</span>
            {QUICK_RULES.map((id) => (
              <button key={id} type="button" className="chip shrink-0" aria-pressed={profile.rules.includes(id)} onClick={() => toggleRule(id)}>{ruleLabel(id)}</button>
            ))}
            {extraRules.map((id) => <button key={id} type="button" className="chip shrink-0" aria-pressed onClick={() => toggleRule(id)}>{ruleLabel(id)}</button>)}
            <button type="button" className="chip shrink-0" onClick={() => setRulesOpen(true)}>More…</button>
          </div>
        </div>

        {known && <BasicsNudge />}

        {!first ? (
          <Notice tone="info">Nothing in the collection fits those filters yet. Try a longer time or fewer restrictions.</Notice>
        ) : (
          <>
            <button type="button" className="block w-full text-left card-interactive rounded-[20px] overflow-hidden mb-6" onClick={() => open(first.rec.recipe.id)}
              aria-label={`${first.rec.recipe.title}, ${first.rec.recipe.minutes} minutes. Open the recipe.`}>
              <RecipeImage recipeId={first.rec.recipe.id} ratio="4/3" priority rounded="rounded-none" />
              <span className="block p-4">
                <span className="block eyebrow text-primary mb-1">{first.headline}</span>
                <span className="block text-title-2 mb-1">{first.rec.recipe.title}</span>
                <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-subhead text-fg-muted mb-2">
                  <span className="inline-flex items-center gap-1"><Clock size={15} aria-hidden /> {first.rec.recipe.minutes} min</span>
                  <span className="inline-flex items-center gap-1"><Users size={15} aria-hidden /> Serves {first.rec.recipe.servings}</span>
                </span>
                <Macros r={first.rec} className="block text-subhead text-fg mb-2" />
                <span className="block text-body">{first.detail}</span>
              </span>
            </button>

            {known && restReady.length > 0 && (
              <section className="mb-7" aria-labelledby="all-ready">
                <h2 id="all-ready" className="section-title mb-1">{first.rec.makeable ? `More you can make now (${restReady.length})` : `Ready to cook (${restReady.length})`}</h2>
                <p className="text-subhead text-fg-muted mb-3">Breakfasts, dinners, sides and snacks. Everything your kitchen covers right now.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {restReady.map((r) => <div key={r.recipe.id} onClickCapture={opened(r.recipe.id)}><MealCard r={r} wide /></div>)}
                </div>
              </section>
            )}

            {known && oneAway.length > 0 && (
              <section className="mb-7" aria-labelledby="one-away">
                <h2 id="one-away" className="section-title mb-1">One ingredient away ({oneAway.length})</h2>
                <div className="card px-4 divide-y divide-line">
                  {(allOneAway ? oneAway : oneAway.slice(0, ONE_AWAY_PREVIEW)).map((r) => (
                    <div key={r.recipe.id} onClickCapture={opened(r.recipe.id)}><MealRow r={r} /></div>
                  ))}
                </div>
                {!allOneAway && oneAway.length > ONE_AWAY_PREVIEW && (
                  <button className="btn btn-ghost w-full mt-1" onClick={() => setAllOneAway(true)}>Show all {oneAway.length}</button>
                )}
              </section>
            )}

            {!known && pool.length > 1 && (
              <section className="mb-6" aria-labelledby="more-picks">
                <h2 id="more-picks" className="section-title mb-1">More ideas</h2>
                <div className="card px-4 divide-y divide-line">
                  {pool.filter((r) => r.recipe.id !== first.rec.recipe.id).slice(0, 5).map((r) => (
                    <div key={r.recipe.id} onClickCapture={opened(r.recipe.id)}><MealRow r={r} /></div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {!known && (
          <Link to="/scan" className="flex items-center gap-3 rounded-[16px] bg-fill px-4 py-3 mb-7 min-h-[64px]" onClick={() => { session.set(FIRST_RUN_KEY, '1'); track('onboarding_step', { step: 'first_meal_snap' }); }}>
            <Camera size={22} className="text-primary shrink-0" aria-hidden />
            <span className="flex-1"><span className="block text-headline">Cook with what you have</span><span className="block text-subhead text-fg-muted">Snap your fridge and we&rsquo;ll show dinners you can make now.</span></span>
          </Link>
        )}
      </div>
      <div className="sticky-actions">
        <div className="max-w-[640px] mx-auto">
          <button className="btn btn-secondary w-full" onClick={() => { session.del(FIRST_RUN_KEY); navigate('/today', { replace: true }); }}>Go to Today</button>
        </div>
      </div>

      <Sheet open={rulesOpen} onClose={() => setRulesOpen(false)} title="Anything you can’t eat?">
        <p className="text-subhead text-fg-muted mb-4">Hard rules, checked against every ingredient. Always check labels if an allergy is serious.</p>
        <RulesEditor rules={profile.rules} other={profile.otherAllergies} onChange={(rules, otherAllergies) => updateProfile({ rules, otherAllergies })} />
        <button className="btn btn-primary w-full mt-5" onClick={() => setRulesOpen(false)}>Done</button>
      </Sheet>
    </main>
  );
}
